import { BevaCalculationInput, BevaCalculationResult } from "@repo/types";
import { AxiosInstance } from "axios";
import { axiosInstance } from "../axios-instance";
import { ENDPOINTS } from "../endpoint";
import { BaseApiService } from "./base-api.service";

// Types for bulk BEVA operations
export interface BevaBulkFilter {
  userId?: number;
  ville?: string;
  type?: string;
  isFinished?: boolean;
  onlyWithoutBeva?: boolean;
}

export interface BevaBulkAllRequest {
  defaultBevaData: BevaCalculationInput;
  batchSize?: number;
  filter?: BevaBulkFilter;
}

export interface BevaBulkInventairesRequest {
  inventaireIds: number[];
  bevaData: BevaCalculationInput;
}

export interface BevaBulkResult {
  message: string;
  totalProcessed: number;
  successful: number;
  failed: number;
  errors: Array<{ inventaireId: number; error: string }>;
  averageBeva: number;
  totalBevaValue: number;
}

export interface BevaInventairesResult {
  message: string;
  successful: number;
  failed: number;
  errors: Array<{ inventaireId: number; error: string }>;
  bevaValues: Array<{ inventaireId: number; beva: number }>;
}

export interface BevaStatistics {
  totalInventaires: number;
  inventairesWithBeva: number;
  inventairesWithoutBeva: number;
  averageBeva: number;
  totalBevaValue: number;
  bevaDistribution: Array<{ range: string; count: number; percentage: number }>;
}

export class BevaService extends BaseApiService {
  constructor(axios: AxiosInstance = axiosInstance) {
    super(ENDPOINTS.BEVA.BASE, axios);
  }

  // Individual BEVA calculations
  async calculateBevaForInventaire(inventaireId: number, data: BevaCalculationInput): Promise<BevaCalculationResult> {
    return this.post<BevaCalculationResult, BevaCalculationInput>(ENDPOINTS.BEVA.BY_INVENTAIRE(inventaireId.toString()), data);
  }

  async calculateBevaForEssence(essenceId: number, data: BevaCalculationInput): Promise<BevaCalculationResult> {
    return this.post<BevaCalculationResult, BevaCalculationInput>(ENDPOINTS.BEVA.BY_ESSENCE(essenceId.toString()), data);
  }

  // Bulk BEVA calculations via BEVA endpoints
  async calculateBevaForAllInventaires(request: BevaBulkAllRequest): Promise<{ data: BevaBulkResult }> {
    const { data } = await this.axios.post(ENDPOINTS.BEVA.BULK.ALL, request);
    return data;
  }

  async calculateBevaForInventaires(request: BevaBulkInventairesRequest): Promise<{ data: BevaInventairesResult }> {
    const { data } = await this.axios.post(ENDPOINTS.BEVA.BULK.INVENTAIRES, request);
    return data;
  }

  async getBevaStatistics(
    startDate?: string,
    endDate?: string,
    code_site?: string,
    includeDrafts?: boolean
  ): Promise<{ data: BevaStatistics }> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (code_site) params.append('code_site', code_site);
    if (includeDrafts === true) params.append('includeDrafts', 'true');

    const { data } = await this.axios.get(
      `${ENDPOINTS.BEVA.BULK.STATISTICS}${params.toString() ? `?${params.toString()}` : ''}`
    );
    return data;
  }

  // Bulk BEVA calculations via Inventaires endpoints (alternative)
  async calculateBulkBevaViaInventaires(request: BevaBulkAllRequest): Promise<{ data: BevaBulkResult }> {
    const { data } = await this.axios.post(ENDPOINTS.BEVA.INVENTAIRES_BULK.BULK, request);
    return data;
  }

  async calculateBevaForInventairesViaInventaires(request: BevaBulkInventairesRequest): Promise<{ data: BevaInventairesResult }> {
    const { data } = await this.axios.post(ENDPOINTS.BEVA.INVENTAIRES_BULK.INVENTAIRES, request);
    return data;
  }
}